import cx from 'classnames';
import {Formik, FormikProps} from 'formik';
import {forEach, isEmpty, isEqual, omit, set} from 'lodash';
import {Fragment, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, ToggleFormik, UnsavedChangesLeavePrompt} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {SpecialDayWorkingHoursItem, WorkingHours as WorkingHoursType} from 'types';
import {workingHoursUtils} from 'utils';

import {SpecialHours} from '../SpecialHours';
import {WeekHours} from '../WeekHours';
import {EMPTY_VALUE, FIELDS} from '../constants';

const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type FormTemplateProps = {
    loading: boolean;
} & FormikProps<WorkingHoursType>;

export const FormTemplate: React.FC<FormTemplateProps> = (props) => {
    const {t} = useTranslation();
    const hasNoChanges = useMemo(() => isEqual(omit(props.values, FIELDS.NEW_SPECIAL_HOURS_ITEM), props.initialValues), [props]);

    const handleSubmit = useCallback(() => {
        props.submitForm();
    }, [props]);

    return (
        <Fragment>
            <ToggleFormik
                name='enabled'
                label={t(T_KEYS_COMMON.ALL.ENABLED)}
                labelPlacement='left'
                className='tw-mb-8 tw-mt-2'
            />
            <div className={cx(
                'tw-flex -2xl:tw-flex-col tw-overflow-hidden',
                'tw-transition-max-height tw-linear tw-delay-150 tw-duration-300 tw-will-change-auto',
                {
                    'tw-max-h-0': !props.values.enabled,
                    'tw-max-h-infinity': props.values.enabled
                })}
            >
                <WeekHours {...props} />
                <SpecialHours {...props} />
            </div>
            <div className='tw-flex tw-items-center tw-justify-end tw-mt-8'>
                <Button
                    disabled={hasNoChanges || !props.isValid}
                    loading={props.loading}
                    className='tw-ml-2'
                    color='primary'
                    onClick={handleSubmit}
                >
                    {t(T_KEYS_COMMON.BUTTONS.SAVE)}
                </Button>
            </div>
            <UnsavedChangesLeavePrompt values={props.values} initialValues={props.initialValues} />
        </Fragment>
    );
};

type Props = {
    workingHours: WorkingHoursType;
    loading: boolean;
    onSubmit: (workingHours: WorkingHoursType) => void;
}

export const BaseWorkingHours: React.FC<Props> = ({workingHours, loading, onSubmit}) => {
    const {t} = useTranslation();

    const formikValue: WorkingHoursType = useMemo(() => workingHours || EMPTY_VALUE, [workingHours]);

    const handleSubmit = useCallback(({enabled, weekHours, specialHours}: WorkingHoursType) => {
        onSubmit({enabled, weekHours, specialHours});
    }, [onSubmit]);

    const validateForm = useCallback((formValues: WorkingHoursType) => {
        const weekHours = {};
        forEach(formValues.weekHours, (value, day) => {
            forEach(value?.intervals, (interval, index) => {
                const {isValid, error} = workingHoursUtils.validateInterval(interval, t);
                if (!isValid) {
                    set(weekHours, `[${day}].${FIELDS.INTERVALS}[${index}]`, error);
                }
            });
        });

        const specialHours = [];
        forEach(formValues.specialHours, (specialHour: SpecialDayWorkingHoursItem, index: number) => {
            const {isValid, error} = workingHoursUtils.validateSpecialDay(specialHour, t);
            if (!isValid) {
                set(specialHours, `[${index}]`, error);
            }
        });

        return {
            ...(!isEmpty(weekHours) && {weekHours}),
            ...(!isEmpty(specialHours) && {specialHours})
        };
    }, [t]);

    return (
        <Formik
            enableReinitialize
            initialValues={formikValue}
            validateOnBlur
            validateOnChange
            validate={validateForm}
            onSubmit={handleSubmit}
        >
            {(props) => (
                <FormTemplate {...props} loading={loading} />
            )}
        </Formik>
    );
};